import React, { useEffect, useState } from "react";
import BasicInfoForm from "./basic-info-form/basic-info-form";
import TrackList from "./track-list/track-list";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Loader from "../common-components/loader/loader";
import routes from "../../routes/routes";
import AlbumPreview from "./album-preview/album-preview";
import { Edit, Headphones, Smile } from "lucide-react";
import { getConfig } from "../../utils/permissions";
import { Steps, Step } from "@/components/ui/steps";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useAuth } from "../../context/auth";
import { SUBSCRIPTION_PLAN_ID } from "../../utils/constants";

const steps = [
  {
    id: 0,
    title: "Basic Info",
    icon: <Edit className="h-4 w-4" />,
  },
  {
    id: 1,
    title: "Track List",
    icon: <Headphones className="h-4 w-4" />,
  },
  {
    id: 2,
    title: "Review",
    icon: <Smile className="h-4 w-4" />,
  },
];

const albumSchema = {
  title: null,
  primaryArtist: null,
  secondaryArtist: null,
  language: null,
  mainGenre: null,
  subGenre: null,
  releaseDate: null,
  productionYear: null,
  label: null,
  UPC: null,
  songs: [],
};

export default function NewRelease() {
  const [current, setCurrent] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const { loginData } = useAuth();

  const [album, setAlbum] = useState(albumSchema);

  const [tracks, setTracks] = useState([]);

  const navigate = useNavigate();

  const { albumId } = useParams();

  const getAlbumDetails = () => axios.get("/albums/" + albumId);

  const apiPrefix = getConfig("privilege.can-use-api-prefix") ? "/admin" : "";

  useEffect(() => console.log("album updated", album), [album]);

  useEffect(() => {
    if (!albumId) return;

    setLoading(true);
    getAlbumDetails()
      .then((response) => {
        let album = response.data.data;
        if (album.title === undefined) album = { ...albumSchema, ...album };
        setAlbum(album);
        setTracks(album.songs);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  const submitAlbum = () => {
    const confirmationMessage =
      "❗Please double check all the details of this release carefully before sending it for review. No change can be performed once you click on OK. Do you want to proceed to submit the release for review?";
    if (window.confirm(confirmationMessage))
      axios
        .patch("/albums/" + albumId + "/submit")
        .then(() => {
          toast.success("Album submitted for review");
          navigate(routes.myReleases);
        })
        .catch((error) => {
          toast.error("Error submitting release", {
            description: error.response.data.error,
          });
        });
  };

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  useEffect(() => {
    const createAlbum = () => axios.post("/albums");

    if (!albumId) {
      // Check subscription
      const isPro =
        loginData?.user?.subscriptionTier === SUBSCRIPTION_PLAN_ID ||
        loginData?.user?.role === "admin";
      if (!isPro) {
        toast.error(
          "You need an Artist Pro subscription to create new releases."
        );
        navigate("/pricing");
        return;
      }

      setLoading(true);
      createAlbum()
        .then((response) => {
          const album = response.data.data.albumInfo;
          navigate("/edit-release/" + album._id);
        })
        .catch((error) => {
          toast.error("Too many drafts", {
            description:
              "Looks like you already have a couple of drafts in your library. Please clear or edit the existing ones.",
          });
          navigate(routes.myReleases);
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, []);

  if (loading)
    return (
      <div className="p-3 md:p-6 w-full h-full flex items-center justify-center">
        <Loader />
      </div>
    );

  if (!albumId) return null;

  const isTrackFieldNull = (fieldName) => {
    return !tracks.every((track) => {
      return (
        track[fieldName] !== undefined &&
        track[fieldName] !== null &&
        track[fieldName] !== ""
      );
    });
  };

  return (
    <div className="p-3 md:p-6 w-full h-full overflow-y-auto">
      {album.status === "Rejected" &&
        album.reviewComments &&
        album.reviewComments !== "" && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              <strong>
                Your album was rejected due to the following reasons:
              </strong>
              <pre className="mt-2 whitespace-pre-wrap font-mono text-sm">
                {album.reviewComments}
              </pre>
            </AlertDescription>
          </Alert>
        )}
      <Steps current={current} className="mb-8">
        {steps.map((item) => (
          <Step key={item.title} title={item.title} icon={item.icon} />
        ))}
      </Steps>
      <div className="steps-content">
        {current === 0 && (
          <BasicInfoForm
            nextStep={next}
            albumId={albumId}
            album={album}
            setAlbum={setAlbum}
            apiPrefix={apiPrefix}
          />
        )}
        {current === 1 && (
          <TrackList
            albumId={albumId}
            tracks={tracks}
            setTracks={setTracks}
            apiPrefix={apiPrefix}
          />
        )}
        {current === 2 && (
          <AlbumPreview album={album} setAlbum={setAlbum} tracks={tracks} />
        )}
      </div>
      <div className="steps-action mt-3 text-right">
        {current === steps.length - 1 && (
          <div className="text-xs text-right mb-1">
            <span>
              By clicking on Submit, you agree to the&nbsp;
              <a href="/terms-of-release" target="_blank">
                terms and conditions of release.
              </a>
            </span>
          </div>
        )}
        {current > 0 && (
          <Button className="mr-2" onClick={() => prev()}>
            Previous
          </Button>
        )}
        {current > 0 && current < steps.length - 1 && (
          <Button
            type="primary"
            onClick={() => next()}
            disabled={
              !tracks.length ||
              isTrackFieldNull("title") ||
              isTrackFieldNull("masterUrl")
            }
          >
            Next
          </Button>
        )}
        {current === steps.length - 1 && (
          <Button type="primary" onClick={submitAlbum}>
            Submit for review
          </Button>
        )}
      </div>
    </div>
  );
}
